import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { useAuth } from "@/lib/auth";
import { roleDisplayNames } from "@/lib/permissions";
import { cn } from "@/lib/utils";
import {
  Bell,
  ChevronUp,
  ClipboardCheck,
  ClipboardList,
  Cog,
  CreditCard,
  FileText,
  Home,
  LayoutDashboard,
  Library,
  LogOut,
  Map,
  MessageSquare,
  Settings,
  Shield,
  Sparkles,
  UserCircle,
  UserCog,
  Users,
  Zap,
} from "lucide-react";
import { Link, useLocation } from "wouter";

const iconMap: Record<string, React.ElementType> = {
  LayoutDashboard,
  Library,
  Home,
  Users,
  UserCircle,
  UserCog,
  FileText,
  Settings,
  ClipboardCheck,
  ClipboardList,
  Bell,
  Shield,
  CreditCard,
  MessageSquare,
  Sparkles,
  Zap,
  Cog,
  Map,
};

export function AppSidebar() {
  const [location, setLocation] = useLocation();
  const { user, navigationItems, logout } = useAuth();

  const mainItems = navigationItems;

  const getInitials = (name?: string) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Sidebar collapsible="icon" className="border-r-0">
      {/* Brand Header - Generous breathing room */}
      <SidebarHeader className="px-6 py-8 group-data-[collapsible=icon]:px-2 group-data-[collapsible=icon]:py-4">
        <Link
          href="/"
          className="flex items-center gap-4 group group-data-[collapsible=icon]:justify-center"
        >
          <div className="flex flex-col group-data-[collapsible=icon]:hidden">
            <span className="font-serif text-xl font-medium tracking-tight text-foreground/90">
              Evooluir
            </span>
            <span className="text-xs text-muted-foreground/70 font-light tracking-wide">
              {user?.roles?.includes("super_admin") && "Super Admin Studio"}
              {user?.roles?.includes("admin") && "Admin Studio"}
              {user?.roles?.includes("coach") && "Coach Studio"}
              {user?.roles?.includes("coach_manager") && "Coach Manager Studio"}
              {!user?.roles && "Studio"}
            </span>
          </div>
          <span className="font-serif text-lg font-medium tracking-tight text-foreground/90 hidden group-data-[collapsible=icon]:inline">
            E
          </span>
        </Link>
      </SidebarHeader>

      {/* Navigation - Soft and spacious */}
      <SidebarContent className="px-4 py-2 group-data-[collapsible=icon]:px-0">
        <SidebarGroup className="group-data-[collapsible=icon]:px-0">
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {mainItems.map((item) => {
                const Icon = iconMap[item.icon] || LayoutDashboard;
                const isActive =
                  location === item.url ||
                  (item.url !== "/" && location.startsWith(item.url + "/"));

                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive}
                      tooltip={item.title}
                      className={cn(
                        "h-12 rounded-xl px-4 transition-all duration-300",
                        "hover:bg-primary/5 hover:scale-[1.02]",
                        isActive && "bg-primary/10 shadow-sm",
                        "group-data-[collapsible=icon]:!h-10 group-data-[collapsible=icon]:!w-10 group-data-[collapsible=icon]:!p-0 group-data-[collapsible=icon]:!gap-0 group-data-[collapsible=icon]:!justify-center group-data-[collapsible=icon]:!mx-auto group-data-[collapsible=icon]:hover:!scale-100 group-data-[collapsible=icon]:rounded-lg",
                      )}
                    >
                      <Link
                        href={item.url}
                        data-testid={`nav-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <div className="relative group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:justify-center">
                          <Icon
                            className={cn(
                              "h-5 w-5 transition-colors duration-300",
                              isActive
                                ? "text-primary"
                                : "text-muted-foreground",
                            )}
                          />
                        </div>
                        <span
                          className={cn(
                            "font-medium transition-colors duration-300 flex-1",
                            isActive
                              ? "text-foreground"
                              : "text-muted-foreground",
                            "group-data-[collapsible=icon]:hidden",
                          )}
                        >
                          {item.title}
                        </span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* User Footer - Soft and welcoming */}
      <SidebarFooter className="px-4 py-6 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:py-3 group-data-[collapsible=icon]:shadow-none shadow-[0_-4px_12px_-4px_rgba(0,0,0,0.08)]">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-4 rounded-2xl evo-gradient-subtle p-4 w-full text-left hover-elevate cursor-pointer group-data-[collapsible=icon]:p-0 group-data-[collapsible=icon]:rounded-full group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:bg-none group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:!w-10 group-data-[collapsible=icon]:!h-10 group-data-[collapsible=icon]:mx-auto"
              data-testid="button-user-menu"
            >
              <Avatar className="h-11 w-11 ring-2 ring-primary/20 shrink-0 group-data-[collapsible=icon]:h-10 group-data-[collapsible=icon]:w-10 group-data-[collapsible=icon]:ring-1">
                {user?.avatarUrl && (
                  <AvatarImage
                    src={user.avatarUrl}
                    alt=""
                    className="object-cover"
                    data-testid="img-sidebar-avatar"
                  />
                )}
                <AvatarFallback className="bg-primary/20 text-primary font-medium">
                  {getInitials(
                    user?.displayName || user?.username || user?.name || "User",
                  )}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
                <span className="text-sm font-medium text-foreground/90 truncate">
                  {user?.displayName ||
                    user?.username ||
                    user?.name ||
                    user?.email ||
                    "Guardian"}
                </span>
                {user?.roles && (
                  <span className="text-xs text-muted-foreground/70 flex items-center gap-1">
                    <Sparkles className="h-3 w-3" />
                    {user.roles.map((r) => roleDisplayNames[r]).join(", ")}
                  </span>
                )}
              </div>
              <ChevronUp className="h-4 w-4 text-muted-foreground group-data-[collapsible=icon]:hidden" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" className="w-56">
            <DropdownMenuItem
              onClick={() => setLocation("/profile")}
              data-testid="menu-profile"
            >
              <UserCircle className="h-4 w-4 mr-2" />
              My Profile
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={logout}
              className="text-destructive focus:text-destructive"
              data-testid="menu-logout"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
